/**
 * Geolocation helper — single source of truth for "where is the user?".
 *
 * Replaces the pre-existing pattern of every component calling
 * `navigator.geolocation.getCurrentPosition` on mount, which produced
 * the repeated-prompt UX bug:
 *   - prompted on every page load
 *   - re-prompted after a denial when the user navigated to a new route
 *   - sometimes fired multiple parallel requests on the same page
 *
 * Behavior:
 *   - `getUserLocation({ requireInteraction })` is the only public entry.
 *   - When `requireInteraction` is false (default), we ONLY fetch the
 *     position if the browser already has `permissions.query` and reports
 *     `granted`. We never auto-trigger the browser prompt without the
 *     user explicitly requesting it.
 *   - When `requireInteraction` is true (i.e. user clicked "Use my location"),
 *     we attempt `getCurrentPosition` directly — that's the only path
 *     that may show the OS prompt. If permission is `denied`, we resolve
 *     to a denial result rather than re-attempting.
 *   - In-flight requests are de-duplicated via a single-flight promise.
 *   - Denials are cached for 24h in `sessionStorage` AND `localStorage`
 *     so a denied user is never re-prompted the same session, and we
 *     don't keep retrying across sessions either.
 *   - Timeouts are fixed at 6s and never auto-retried.
 *
 * The helper resolves to:
 *   { ok: true, lat, lng, accuracy } on success
 *   { ok: false, reason } where reason in:
 *     "unsupported", "denied", "timeout", "error", "no_permission"
 */

const SESSION_DENIED = "tt:geo:session-denied";
const PERSIST_DENIED_UNTIL = "tt:geo:denied-until";
const DENY_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const TIMEOUT_MS = 6000;
const REPORT_TS_KEY = "tt:geo:last-report";
const REPORT_MIN_INTERVAL_MS = 10 * 60 * 1000; // 10 min

let _inflight = null; // single-flight promise

// Fire-and-forget: report the device's last-known location so the
// backend can send "followed truck is 2 miles away" pushes. Keyed on the
// same follower_id as follows + push subs. Throttled to 10 min.
function _maybeReportLocation(res) {
  if (!res?.ok) return;
  try {
    const last = Number(localStorage.getItem(REPORT_TS_KEY) || 0);
    if (Number.isFinite(last) && Date.now() - last < REPORT_MIN_INTERVAL_MS) return;
    localStorage.setItem(REPORT_TS_KEY, String(Date.now()));
  } catch { /* private mode — still try to report once */ }
  Promise.all([import("./api"), import("./follows")])
    .then(([{ default: api }, { getFollowerId }]) =>
      api.post("/push/location", {
        follower_id: getFollowerId(),
        lat: res.lat,
        lng: res.lng,
      }))
    .catch(() => { /* best effort */ });
}

function _safeSession(key, val) {
  try {
    if (val === undefined) return sessionStorage.getItem(key);
    if (val === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, val);
  } catch {
    return null;
  }
}

function _safeLocal(key, val) {
  try {
    if (val === undefined) return localStorage.getItem(key);
    if (val === null) localStorage.removeItem(key);
    else localStorage.setItem(key, val);
  } catch {
    return null;
  }
}

function _isCachedDenied() {
  if (_safeSession(SESSION_DENIED) === "1") return true;
  const persisted = _safeLocal(PERSIST_DENIED_UNTIL);
  if (!persisted) return false;
  const until = Number(persisted);
  if (!Number.isFinite(until)) return false;
  if (Date.now() < until) return true;
  // expired
  _safeLocal(PERSIST_DENIED_UNTIL, null);
  return false;
}

function _markDenied() {
  _safeSession(SESSION_DENIED, "1");
  _safeLocal(PERSIST_DENIED_UNTIL, String(Date.now() + DENY_COOLDOWN_MS));
}

/**
 * Resolves to the current Permissions API state for geolocation, or
 * "unknown" if the API isn't available (older Safari, in-app webviews).
 * Never throws.
 */
export async function getPermissionState() {
  if (typeof navigator === "undefined") return "unsupported";
  if (!("geolocation" in navigator)) return "unsupported";
  if (!navigator.permissions || !navigator.permissions.query) return "unknown";
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state; // "granted" | "denied" | "prompt"
  } catch {
    return "unknown";
  }
}

function _doGetCurrentPosition() {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve({ ok: false, reason: "unsupported" });
      return;
    }
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        finish({
          ok: true,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => {
        // err.code: 1=denied, 2=unavailable, 3=timeout
        if (err && err.code === 1) {
          _markDenied();
          finish({ ok: false, reason: "denied" });
        } else if (err && err.code === 3) {
          finish({ ok: false, reason: "timeout" });
        } else {
          finish({ ok: false, reason: "error" });
        }
      },
      { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: 60_000 },
    );
  });
}

/**
 * Returns the user's current position, or a structured failure.
 *
 * @param {object} opts
 * @param {boolean} opts.requireInteraction
 *   If true, the caller is responding to an explicit user gesture
 *   ("Use my location" button) and we may show the OS prompt.
 *   If false (default), we only return a position if permission has
 *   ALREADY been granted — never auto-trigger the prompt.
 */
export async function getUserLocation({ requireInteraction = false } = {}) {
  // Single-flight: any concurrent caller waits on the in-flight promise.
  if (_inflight) return _inflight;

  // Cached denial: never re-prompt within 24h or within the session.
  if (_isCachedDenied()) {
    return { ok: false, reason: "denied" };
  }

  const state = await getPermissionState();
  if (state === "unsupported") return { ok: false, reason: "unsupported" };
  if (state === "denied") {
    _markDenied();
    return { ok: false, reason: "denied" };
  }

  if (state === "granted") {
    _inflight = _doGetCurrentPosition()
      .then((res) => { _maybeReportLocation(res); return res; })
      .finally(() => {
        _inflight = null;
      });
    return _inflight;
  }

  // state === "prompt" or "unknown"
  if (!requireInteraction) {
    // The caller is just decorating a page; we don't trigger the OS
    // prompt without an explicit gesture. The caller can render a
    // "Use my location" CTA that calls us again with requireInteraction.
    return { ok: false, reason: "no_permission" };
  }

  _inflight = _doGetCurrentPosition()
    .then((res) => { _maybeReportLocation(res); return res; })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/** Useful for components that show a "permission denied" coachmark. */
export function isLocationDeniedCached() {
  return _isCachedDenied();
}

/** Test-only: clear the deny cache. */
export function _clearDenyCache() {
  _safeSession(SESSION_DENIED, null);
  _safeLocal(PERSIST_DENIED_UNTIL, null);
  _inflight = null;
}
